// Updated BuyerDetails.js
import React, { useState, useEffect, Component } from 'react'
import './register.css'
import { BuyerUpdate, GetBuyer } from '../../../../api/Webservices'
import { useRegistration } from '../../../../context/RegistrationContext'

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong.</h2>
          <p>Please try refreshing the page or contact support.</p>
          <button onClick={() => this.setState({ hasError: false })}>
            Try Again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const BuyerDetails = ({ onProceed, onBack, eventData }) => {
  const [buyerType, setBuyerType] = useState('individual')
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [buyerFormFields, setBuyerFormFields] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { registrationData, updateRegistrationData } = useRegistration()
  const draftKey = `buyerDraft_${eventData?.event?.id || 'unknown'}`

  // Parse JSON fields safely
  const parseFields = (fields) => {
    try {
      let result = fields
      while (typeof result === 'string') {
        result = JSON.parse(result.replace(/^"|"$/g, '').replace(/\\"/g, '"'))
      }
      return Array.isArray(result) ? result : []
    } catch (err) {
      console.error('❌ JSON parse failed:', err, 'Original string:', fields)
      return []
    }
  }

  // Fetch buyer data and set form fields
  useEffect(() => {
    const fetchAndSetData = async () => {
      setIsLoading(true) // Set loading to true at the start
      const savedDraftIds = localStorage.getItem('draft_ids')
      let paymentId = null

      try {
        // Determine paymentId
        if (savedDraftIds) {
          const parsedDraftIds = JSON.parse(savedDraftIds)
          paymentId =
            parsedDraftIds.attendees_payments_id ||
            registrationData?.attendees_payments_id
        } else {
          paymentId = registrationData?.attendees_payments_id
        }

        // If paymentId exists, fetch buyer data
        if (paymentId) {
          try {
            const response = await GetBuyer(paymentId)
            console.log(response, 'res')

            if (response.data.success && response.data.data?.length > 0) {
              const buyerDetailsRaw = response.data.data[0].buyer_details
              const buyerData = JSON.parse(buyerDetailsRaw)
              console.log(buyerData, 'buyerData')

              const initialFormData = buyerData.custom_fields.reduce(
                (acc, field) => ({
                  ...acc,
                  [field.id]: field.value || '',
                }),
                {}
              )

              setFormData(initialFormData)
              setBuyerType(buyerData.buyer_type || 'individual')
              updateRegistrationData({
                buyerData: initialFormData,
                attendees_payments_id: response.data.attendee_payments_id,
              })
            } else {
              setErrorMessage('No buyer data found for this payment ID.')
            }
          } catch (error) {
            console.error('Failed to fetch buyer details:', error)
            setErrorMessage('Failed to load buyer details. Please try again.')
          }
        }

        // Load buyer form fields if eventData is available
        if (eventData?.settings?.allow_buyers === 1) {
          const buyerFormId = eventData.settings.buyer_form
          const forms = eventData?.buyerForm || []
          console.log(
            '🔍 Looking for form with ID:',
            buyerFormId,
            'in forms:',
            forms
          )

          const matchingForm = forms.find(
            (form) => form.id === buyerFormId && form.form_type === 'Buyer'
          )
          if (matchingForm && matchingForm.fileds) {
            console.log('✅ Found matching Buyer form:', matchingForm)
            const parsedFields = parseFields(matchingForm.fileds)
            console.log(
              '✅ Parsed fields:',
              parsedFields,
              'Typeof:',
              typeof parsedFields,
              'Is Array:',
              Array.isArray(parsedFields)
            )

            if (
              Array.isArray(parsedFields) &&
              parsedFields.every(
                (field) =>
                  typeof field === 'object' &&
                  field !== null &&
                  field.id &&
                  field.label
              )
            ) {
              setBuyerFormFields(parsedFields)
              console.log('✅ Set buyerFormFields to:', parsedFields)
            } else {
              console.warn(
                '⚠️ Parsed fields is not an array or contains invalid entries:',
                parsedFields
              )
              setBuyerFormFields([])
              setErrorMessage('Invalid form fields configuration.')
            }
          } else {
            console.warn(
              '⚠️ No matching form found for Buyer type + ID',
              buyerFormId
            )
            setBuyerFormFields([])
            setErrorMessage('No buyer form available for this event.')
          }
        } else {
          console.warn('⚠️ eventData invalid or buyers not allowed')
          setBuyerFormFields([])
          setErrorMessage('Buyers not allowed for this event.')
        }
      } catch (e) {
        console.error('Invalid draft_ids or buyer data:', e)
        localStorage.removeItem('draft_ids')
        setErrorMessage('Invalid data encountered. Please try again.')
      } finally {
        setIsLoading(false) // Set loading to false after all operations
      }
    }

    fetchAndSetData()
  }, [eventData, registrationData?.attendees_payments_id])

  const handleInputChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }))
    if (errors[fieldId]) {
      setErrors((prev) => ({ ...prev, [fieldId]: '' }))
    }
    setErrorMessage('')
  }

  const validateForm = () => {
    const newErrors = {}
    if (Array.isArray(buyerFormFields)) {
      buyerFormFields.forEach((field) => {
        if (
          field.required &&
          (!formData[field.id] || !formData[field.id].trim())
        ) {
          newErrors[field.id] = `${field.label} is required`
        } else if (
          field.type === 'email' &&
          formData[field.id] &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field.id])
        ) {
          newErrors[field.id] = 'Please enter a valid email address'
        }
      })
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleProceed = async () => {
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage('')

    const buyerData = {
      buyer_type: buyerType,
      custom_fields: Array.isArray(buyerFormFields)
        ? buyerFormFields.map((field) => ({
            id: field.id,
            type: field.type,
            label: field.label,
            name: field.name || field.id.split('-').pop(),
            value: formData[field.id] || '',
          }))
        : [],
    }
    console.log('Proceeding with buyerData:', buyerData)

    const startTime = Date.now()

    try {
      if (registrationData?.success) {
        const buyerPayload = {
          id: registrationData.attendees_payments_id,
          event_id:
            localStorage.getItem('eventId') || eventData?.event?.id || 1,
          buyer_details: buyerData,
          first_name:
            buyerData.custom_fields.find((f) => f.name === 'first_name')
              ?.value || '',
          email:
            buyerData.custom_fields.find((f) => f.type === 'email')?.value ||
            '',
        }
        await BuyerUpdate(buyerPayload)
        console.log('Buyer data saved successfully:', buyerPayload)

        const draftIds = {
          attendees_payments_id: buyerPayload.id,
          attendee_ids: registrationData.attendee_ids || [],
        }
        localStorage.setItem('draft_ids', JSON.stringify(draftIds))
      }

      const elapsed = Date.now() - startTime
      const remaining = 1000 - elapsed
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining))
      }

      onProceed(buyerData, 'attendees')
    } catch (err) {
      console.error('Error saving buyer data:', err)
      setErrorMessage(
        err.response?.data?.message ||
          'Failed to save buyer details. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ErrorBoundary>
      <div className="sticky-event-header">
        <div className="sticky-event-content">
          <h1 className="sticky-event-title">Buyer Details</h1>
          <div className="sticky-event-details">
            <div className="sticky-event-item">
              <span>Primary contact information</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card-content">
        <div className="content-header">
          <h3 className="content-title">Buyer Information</h3>
        </div>

        {isLoading ? (
          <div className="loader-container">
            <div className="spinner"></div>
            <p>Loading buyer form...</p>
          </div>
        ) : eventData?.settings?.allow_buyers === 1 &&
          Array.isArray(buyerFormFields) &&
          buyerFormFields.length > 0 ? (
          <div className="attendee-form">
            {errorMessage && (
              <div
                className="error-message"
                style={{
                  color: '#ef4444',
                  marginBottom: '16px',
                  fontSize: '14px',
                }}
              >
                {errorMessage}
              </div>
            )}
            {buyerFormFields.map((field) => (
              <div key={field.id} className="form-group">
                <label
                  htmlFor={field.id}
                  className="form-label"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                  }}
                >
                  {field.label}
                  {field.required && (
                    <span
                      style={{
                        color: '#ef4444',
                        fontSize: '16px',
                        fontWeight: 'bold',
                      }}
                    >
                      *
                    </span>
                  )}
                </label>
                <input
                  type={field.type || 'text'}
                  id={field.id}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '8px 16px',
                    border: errors[field.id]
                      ? '2px solid #ef4444'
                      : '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    backgroundColor: errors[field.id] ? '#fef2f2' : '#fafafa',
                  }}
                  placeholder={field.placeholder}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  disabled={isSubmitting}
                />
                {errors[field.id] && (
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#ef4444',
                      marginTop: '4px',
                      display: 'block',
                      fontWeight: '500',
                    }}
                  >
                    {errors[field.id]}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p>No buyer form available for this event.</p>
        )}
      </div>

      <div className="sticky-bottom">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="total-section">
          <div className="total-info">
            <div className="total-details text-gray-700">
              Primary contact information
            </div>
          </div>
          {!isLoading &&
            eventData?.settings?.allow_buyers === 1 &&
            Array.isArray(buyerFormFields) &&
            buyerFormFields.length > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-outline"
                  onClick={onBack}
                  disabled={isSubmitting}
                >
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleProceed}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span
                      className="spinner"
                      style={{
                        display: 'inline-block',
                        width: '20px',
                        height: '20px',
                        border: '2px solid #fff',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    ></span>
                  ) : (
                    'Proceed'
                  )}
                </button>
              </div>
            )}
        </div>
      </div>

    
  
    </ErrorBoundary>
  )
}

export default BuyerDetails